/**
 * One normalization function shared by the XML serializer and the find
 * matcher. Without this, a model quotes text exactly as a read printed it and
 * still gets `find_none`. SPECIFICATION.md 7.1.
 */

import type { PMNode } from './types.ts'

const CURLY_QUOTES = /[‘’‚‛′]/g
const CURLY_DQUOTES = /[“”„‟″]/g
const DASHES = /[‐‑‒–—―−]/g
const ODD_SPACES = /[   -   　]/g

/**
 * Normalised text plus an index map back to the raw string.
 *
 * `starts[i]` / `ends[i]` give the raw range of normalised character `i`. The
 * map is built per grapheme cluster because NFC is not one-to-one: it
 * contracts (a letter plus a combining mark becoming one character, Hangul
 * jamo) and for some code points it expands. A per-code-unit map would be
 * wrong in both directions.
 *
 * Everything that matches or edits text goes through this, so matching and
 * surgery can never disagree about where a range is. BUG-009.
 */
export interface NormalizedMap {
  out: string
  starts: number[]
  ends: number[]
}

export function normalizeWithMap(raw: string): NormalizedMap {
  const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' })
  let out = ''
  const starts: number[] = []
  const ends: number[] = []
  let lastWasSpace = false

  for (const { segment, index } of segmenter.segment(raw)) {
    let piece = segment.normalize('NFC')
    piece = piece.replace(ODD_SPACES, ' ')
    piece = piece.replace(CURLY_QUOTES, "'")
    piece = piece.replace(CURLY_DQUOTES, '"')
    piece = piece.replace(DASHES, '-')

    const rawStart = index
    const rawEnd = index + segment.length

    if (/^\s+$/.test(piece)) {
      if (lastWasSpace) {
        // A collapsed run stays one normalised space whose raw range grows to
        // cover everything it swallowed.
        if (ends.length) ends[ends.length - 1] = rawEnd
        continue
      }
      out += ' '
      starts.push(rawStart)
      ends.push(rawEnd)
      lastWasSpace = true
      continue
    }

    lastWasSpace = false
    // One entry per UTF-16 code unit, because `out` is searched with indexOf
    // and that returns code-unit indices. Iterating code points here would
    // desync the map on any astral character (an emoji, most CJK extensions),
    // which is exactly how a lone surrogate used to escape.
    //
    // Every unit of a cluster maps back to the whole cluster, so a boundary can
    // only ever fall between clusters.
    for (const ch of piece) {
      out += ch
      for (let k = 0; k < ch.length; k++) {
        starts.push(rawStart)
        ends.push(rawEnd)
      }
    }
  }

  return { out, starts, ends }
}

/** One implementation, so the matcher and the serializer cannot drift apart. */
export function normalizeForFind(s: string): string {
  return normalizeWithMap(s).out
}

/** Translate a normalised range to raw offsets. Boundaries land on clusters. */
export function toRawRange(map: NormalizedMap, start: number, end: number): { from: number; to: number } {
  if (start >= map.starts.length) {
    const tail = map.ends.length ? map.ends[map.ends.length - 1] : 0
    return { from: tail, to: tail }
  }
  const from = map.starts[start]
  const to = end > 0 ? map.ends[Math.min(end, map.ends.length) - 1] : from
  return { from, to }
}

/**
 * The text a node contributes to matching. Chips contribute their display text
 * so a quoted phrase spanning a mention still resolves.
 */
export function inlineText(node: PMNode): string {
  switch (node.type) {
    case 'text':
      return node.text ?? ''
    case 'mention':
      return '@' + (node.attrs?.label ?? 'person')
    case 'date':
      return node.attrs?.value ?? ''
    case 'dropdown':
      return node.attrs?.label ?? ''
    case 'embed':
      return ''
    default:
      if (!node.content) return node.text ?? ''
      return node.content.map(inlineText).join('')
  }
}

/** Concatenated inline text of a block, for find targets and previews. */
export function blockText(node: PMNode): string {
  return inlineText(node)
}

export interface ChildSpan {
  node: PMNode
  /** Raw offsets of this child's contribution within the block's raw text. */
  start: number
  end: number
}

export interface BlockRawText {
  raw: string
  children: ChildSpan[]
}

/**
 * The block's raw text built ONCE from its children, with each child's span
 * recorded. Matching previously used the concatenation while surgery summed
 * per-child normalisations, which differ whenever whitespace collapses or a
 * sequence composes across a child boundary. BUG-009.
 */
export function blockRawText(node: PMNode): BlockRawText {
  let raw = ''
  const children: ChildSpan[] = []
  for (const child of node.content ?? []) {
    const text = inlineText(child)
    children.push({ node: child, start: raw.length, end: raw.length + text.length })
    raw += text
  }
  return { raw, children }
}

/**
 * Short preview used by the outline projection, cut at a word boundary.
 *
 * `max` counts CODE POINTS, not UTF-16 code units. Slicing by code unit cut an
 * astral character -- an emoji, most CJK extensions -- in half and emitted a
 * lone surrogate straight into the outline XML. BUG-019.
 *
 * This is a display-only cut and takes no part in `normalizeWithMap`, whose
 * index map stays one entry per code unit so `indexOf` results keep lining up.
 */
export function preview(node: PMNode, max = 10): string {
  const text = blockText(node).trim()
  const points = Array.from(text)
  if (points.length <= max) return text
  const cut = points.slice(0, max).join('')
  // A space is always one code unit, so slicing at one is boundary-safe.
  const sp = cut.lastIndexOf(' ')
  const head = sp > max * 0.5 ? cut.slice(0, sp) : cut
  return head + '…'
}
